import { RegEdComplianceContentType, submitRegEdCompliance } from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { Modifiers } from 'react-day-picker-old/types/common';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { postNotification } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import {
	calcScheduleCampaignCalendarDateRange,
	convertDurationFromString,
	getScheduleTimeOptions,
	getSendOnBehalfWaitTime,
} from '../../../../models/UiUtils';
import {
	useBlogUpdateMutation,
	useBlogUpdateScheduleMutation,
	useCreateBlogPostMutation,
	useInfiniteReportsBlogsQuery,
} from '../../../../queries';
import { Button } from '../../../components/Button';
import { CampaignDayPicker } from '../../../components/CampaignDayPicker';
import { DefaultCampaignCalendarBlogStatuses } from '../../../components/campaigns/CampaignCalendar/models';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { ISelectBoxOption } from '../../../components/SelectBox';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemIndicator,
	ComboboxList,
} from '../../../components-v2/Combobox';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../components-v2/Select';
import { BlogsCircleGraphic } from '../../../components/svgs/graphics/BlogsCircleGraphic';
import { bs } from '../../../styles/styles';
import { CampaignSessionManager, SessionProgressUpdate } from '../../shared/AIScheduleSuccess/campaignSessionManager';
import { BlogPostContext } from '../blogPostContext';
import { IBlogPostSchedulerProps } from '../Post/models';
import { BlogCampaignCompletedListItem } from './BlogCampaignCompletedListItem';
import { BlogCampaignPendingListItem } from './BlogCampaignPendingListItem';
import { BlogCampaignScheduledListItem } from './BlogCampaignScheduledListItem';
import { ScheduleDateInput } from './ScheduleDateInput';
import { NUM_WEEKS_SHOWN_IN_CAL, reducer } from './scheduleReducer';
import { styleSheet } from './styles';

const handleCampaignSessionNavigation = (
	progress: SessionProgressUpdate | null,
	blogPost: Api.IBlogPost,
	fullscreenModal: any
) => {
	if (progress?.isCampaignComplete) {
		CampaignSessionManager.clearSession();
		fullscreenModal.dismissModal();
		return;
	}

	const sessionId = progress?.session?.id ?? CampaignSessionManager.getSession()?.id;
	const locationState = sessionId ? { blogPost, sessionId } : { blogPost };

	fullscreenModal.history.replace({
		pathname: `/blog/post/success`,
		state: locationState,
	});
};

const handleBlogPostScheduleSuccess = (
	blog: Api.IBlogPost,
	fullscreenModal: any,
	onCallback?: (blog: Api.IBlogPost) => boolean
) => {
	const isSendNow = blog.scheduledSendDate ? moment(blog.scheduledSendDate).isSame(moment(), 'hour') : false;
	const hasCampaignSession = CampaignSessionManager.hasActiveSession();

	const aiContext = CampaignSessionManager.createAIContextFromBlogData(
		blog.title || '',
		blog.content || { document: '', source: '' }
	);

	let sessionProgress: SessionProgressUpdate | null = null;
	const scheduledDate = blog.scheduledSendDate ? new Date(blog.scheduledSendDate) : undefined;

	if (aiContext) {
		if (hasCampaignSession) {
			sessionProgress = CampaignSessionManager.updateSessionProgress('blog', aiContext, scheduledDate);
		} else {
			CampaignSessionManager.startSession('blog', aiContext, scheduledDate);
		}
	}

	if (!onCallback?.(blog)) {
		// If it's send now for primary resource with no prior session, dismiss immediately
		const isPrimarySendNow = CampaignSessionManager.isPrimaryResourceSendNow('blog', isSendNow);
		if (!hasCampaignSession && isPrimarySendNow) {
			CampaignSessionManager.clearSession();
			fullscreenModal.dismissModal();
			return;
		}

		const hasActiveSession = CampaignSessionManager.hasActiveSession();

		if (hasActiveSession) {
			// If campaign is complete or it's send now, dismiss modal
			if (sessionProgress?.isCampaignComplete || isPrimarySendNow) {
				CampaignSessionManager.clearSession();
				fullscreenModal.dismissModal();
				return;
			}

			handleCampaignSessionNavigation(sessionProgress, blog, fullscreenModal);
			return;
		}

		fullscreenModal.history.replace({
			pathname: `/blog/post/success`,
			state: { blogPost: blog },
		});
	}
};

interface IProps extends IBlogPostSchedulerProps, IImpersonationContextComponentProps {
	className?: string;
	sendFrom?: Api.ISendEmailFrom;
	initialSelectedDate?: Date;
	isSubmitting?: boolean;
}

const ScheduleSelectBoxOptions: ISelectBoxOption<'now' | 'schedule'>[] = [
	{
		title: 'Publish Now',
		value: 'now',
	},
	{
		title: 'Schedule for later',
		value: 'schedule',
	},
];

export const ScheduleBlogPost = inject(ImpersonationContextKey)(function ScheduleBlogPost({
	className,
	sendFrom,
	isSubmitting,
	onScheduled,
	onCreated,
	onUpdated,
	impersonationContext,
}: IProps) {
	const fullscreenModal = useFullscreenModal();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('ScheduleBlogPost');
	const { blogPost, setBlogPost, regEdMetadata, regEdRevisesApprovalRequestId } = React.useContext(BlogPostContext);
	const initialSelectedDate = blogPost?.scheduledSendDate ? new Date(blogPost.scheduledSendDate) : new Date();

	// Isolated: the blog is already scheduled, so a submission failure must not surface as a scheduling
	// error — the advisor can resubmit.
	const submitRegEdComplianceForBlog = (blog: Api.IBlogPost) =>
		submitRegEdCompliance({
			userSession,
			parentType: RegEdComplianceContentType.Blog,
			parentId: blog.id,
			approvalRequestId: blog.approvalRequests?.[0]?.id,
			metadata: regEdMetadata,
			revisesApprovalRequestId: regEdRevisesApprovalRequestId,
			impersonationContext: impersonationContext?.toJs(),
			logApiError,
		});

	const updateBlogPostScheduleMutation = useBlogUpdateScheduleMutation({
		impersonationContext: impersonationContext?.toJs(),
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('UpdateBlogPostSchedule-Error', error);
		},
		onSuccess: async blog => {
			setBlogPost(blog);
			postNotification({
				info: blog,
				topic: Topics.EDIT_BLOG_POST,
			});

			await submitRegEdComplianceForBlog(blog);
			handleBlogPostScheduleSuccess(blog, fullscreenModal, onScheduled);
		},
	});
	const updateBlogPostMutation = useBlogUpdateMutation({
		impersonationContext: impersonationContext?.toJs(),
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('UpdateBlogPost-Error', error);
		},
		onSuccess: blog => {
			setBlogPost(blog);
			postNotification({
				info: blog,
				topic: Topics.EDIT_BLOG_POST,
			});
			if (!onUpdated?.(blog)) {
				fullscreenModal.history.push(`/blog/post/edit/${blogPost.id}`);
			}
		},
	});

	const createBlogPostMutation = useCreateBlogPostMutation({
		impersonationContext: impersonationContext?.toJs(),
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('CreateBlogPost-Error', error);
		},
		onSuccess: async blog => {
			setBlogPost(blog);
			postNotification({
				info: blog,
				topic: Topics.EDIT_BLOG_POST,
			});

			await submitRegEdComplianceForBlog(blog);
			handleBlogPostScheduleSuccess(blog, fullscreenModal, onCreated);
		},
	});

	const [isDateInputValid, setIsDateInputValid] = React.useState(true);
	const [state, dispatch] = React.useReducer(reducer, { initialSelectedDate }, init => {
		const selectedDate = init.initialSelectedDate ?? new Date();
		return {
			selectedDate,
			dateRange: calcScheduleCampaignCalendarDateRange({
				selectedDate,
				numWeeks: NUM_WEEKS_SHOWN_IN_CAL,
			}),
			month: new Date(selectedDate),
			sendNow: moment(selectedDate).isSame(moment(), 'day'),
		};
	});

	const reportsBlogsQuery = useInfiniteReportsBlogsQuery({
		impersonationContext: impersonationContext?.toJs(),
		endDate: state.dateRange.endDate.toISOString(),
		startDate: state.dateRange.startDate.toISOString(),
		sendFromUserId: blogPost.sendFromUserId || userSession.user.id,
		status: DefaultCampaignCalendarBlogStatuses,
		pageSize: 25,
	});

	const completedAndStartedCampaigns = (reportsBlogsQuery.data?.pages?.[0].values ?? []).filter(
		campaign => campaign.status === Api.BlogStatus.Completed
	);
	const completedCampaignDates = completedAndStartedCampaigns.map(campaign => new Date(campaign.scheduledSendDate));
	const queuedCampaigns = (reportsBlogsQuery.data?.pages?.[0].values ?? []).filter(
		campaign => campaign.status === Api.BlogStatus.Queued || campaign.status === Api.BlogStatus.Pending
	);
	const queuedCampaignDates = queuedCampaigns.map(campaign => new Date(campaign.scheduledSendDate!));

	const modifiers = {
		hasCampaign: [...completedCampaignDates, ...queuedCampaignDates],
		hasCompletedCampaign: completedCampaignDates,
		hasQueuedCampaign: queuedCampaignDates,
	} as Omit<Modifiers, 'today' | 'outside'>;
	/* ---------- */
	const allOptions = getScheduleTimeOptions(
		0,
		userSession,
		undefined,
		moment(state.selectedDate),
		impersonationContext,
		false,
		15
	);
	let timeOptions = allOptions;
	const nowMoment = moment();
	const days = convertDurationFromString(userSession?.account?.preferences?.sendOnBehalfWaitTime)?.days ?? 2;
	if (
		(sendFrom === 'ContactOwner' || sendFrom === 'SelectedUser') &&
		moment(state.selectedDate).isSame(getSendOnBehalfWaitTime(userSession), 'day') &&
		days !== 2
	) {
		timeOptions = allOptions.filter(({ value }) => {
			const thenMoment = getSendOnBehalfWaitTime(userSession)
				.set('hours', Math.floor(value / 60))
				.set('minutes', value % 60);

			return thenMoment.isAfter(getSendOnBehalfWaitTime(userSession));
		});
	} else if (moment(state.selectedDate).isSame(nowMoment, 'day')) {
		timeOptions = allOptions.filter(({ value }) => {
			const thenMoment = moment(nowMoment)
				.set('hours', Math.floor(value / 60))
				.set('minutes', value % 60);
			return thenMoment.isAfter(nowMoment);
		});
	}
	const selectedDateMoment = moment(state.selectedDate);
	const selectedTotalMinutes = selectedDateMoment.get('hours') * 60 + selectedDateMoment.get('minutes');
	const validSelectedHour = timeOptions.find(x => x.value === selectedTotalMinutes) ?? {
		isDefault: true,
		title: 'Select a time',
		value: -1,
	};
	/* ---------- */

	const shouldShowScheduleTypeSelector = moment(state.selectedDate).isSame(moment(), 'day') && timeOptions.length > 0;
	const showIsLoading =
		isSubmitting ||
		updateBlogPostMutation.isPending ||
		createBlogPostMutation.isPending ||
		updateBlogPostScheduleMutation.isPending;
	const disableSendButton = (!state.sendNow && validSelectedHour.isDefault) || showIsLoading || !isDateInputValid;

	const handleSendNowClick = () => {
		const nextPost: Api.IBlogPost = {
			...blogPost,
			scheduledSendDate: new Date().toISOString(),
		};
		if (blogPost?.id) {
			updateBlogPostScheduleMutation.mutate({ post: nextPost });
			return;
		}
		createBlogPostMutation.mutate({ post: nextPost });
	};

	const handleScheduleClick = () => {
		const nextPost: Api.IBlogPost = {
			...blogPost,
			scheduledSendDate: state.selectedDate.toISOString(),
		};
		if (blogPost?.id) {
			// Use the schedule mutation instead of the update mutation for proper navigation
			updateBlogPostScheduleMutation.mutate({ post: nextPost });
		} else {
			createBlogPostMutation.mutate({ post: nextPost });
		}
	};

	const hanldleChangeDateClick = () => {
		const nextPost: Api.IBlogPost = {
			...blogPost,
			scheduledSendDate: state.selectedDate.toISOString(),
		};

		updateBlogPostMutation.mutate({ post: nextPost });
	};

	return (
		<div className={`${className ?? ''} ${css(styleSheet.mainContainer)}`}>
			<MultiContainerHeader
				fullscreenHeader='Schedule Blog Post'
				onFullscreenRequestClose={ev => {
					ev.preventDefault();
					CampaignSessionManager.clearSession();
					fullscreenModal.dismissModal();
				}}
			/>
			<header className={css(bs.flex, bs.itemsCenter, styleSheet.header)}>
				<figure className={css(styleSheet.figure)}>
					<BlogsCircleGraphic />
				</figure>
				<section className={css(styleSheet.section)}>
					<h2>When do you want to publish this blog?</h2>
				</section>
			</header>
			<div
				className={css(
					bs.flex,
					bs.itemsCenter,
					bs.gap8,
					bs.borderGray300,
					bs.border0,
					bs.borderB,
					bs.borderSolid,
					bs.h320,
					bs.pt5,
					bs.pb2
				)}
			>
				<CampaignDayPicker
					modifiers={modifiers}
					onDayClick={day => {
						dispatch({ type: 'calendarDaySelected', payload: day });
					}}
					onDateRangeChange={dateRange => {
						dispatch({
							type: 'setDateRange',
							payload: dateRange,
						});
					}}
					onMonthChange={date => {
						dispatch({ type: 'monthNavigated', payload: date });
					}}
					month={state.month}
					numWeeksShown={NUM_WEEKS_SHOWN_IN_CAL}
					selectedDay={state.selectedDate}
					allowPastDates={true}
				/>
				<div className={css(bs.flexGrow, bs.hFull, bs.relative)}>
					<div className={css(bs.hFull, bs.overflowAuto, bs.flex, bs.flexCol, bs.gap2)}>
						{queuedCampaigns.length > 0 ? (
							<div>
								<div className={css(bs.textHeader, bs.textXs, bs.uppercase, bs.mb2)}>Planned Blogs</div>
								<div className={css(bs.flex, bs.flexCol, bs.gap5)}>
									{queuedCampaigns.map((campaign, i) =>
										campaign.status === Api.BlogStatus.Queued ? (
											<BlogCampaignScheduledListItem key={i} report={campaign} index={i} />
										) : (
											<BlogCampaignPendingListItem key={i} report={campaign} index={i} />
										)
									)}
								</div>
							</div>
						) : null}
						{completedAndStartedCampaigns.length > 0 ? (
							<div>
								<div className={css(bs.textHeader, bs.textXs, bs.uppercase, bs.mb2)}>Completed Blogs</div>
								<div className={css(bs.flex, bs.flexCol, bs.gap5)}>
									{completedAndStartedCampaigns.map((campaign, i) => (
										<BlogCampaignCompletedListItem key={i} report={campaign} index={i} />
									))}
								</div>
							</div>
						) : null}
						<div
							className={css(
								bs['top-1-2'],
								bs.absolute,
								bs.transform,
								bs['left-1-2'],
								bs['-translate-x-1-2'],
								bs['-translate-y-1-2']
							)}
						>
							{!reportsBlogsQuery.isSuccess ? <LoadingSpinner type='large' /> : null}
							{reportsBlogsQuery.isSuccess && queuedCampaigns.length === 0 && completedAndStartedCampaigns.length === 0
								? 'No scheduled Blogs'
								: null}
						</div>
					</div>
				</div>
			</div>
			<footer className={css(bs.flex, bs.itemsCenter, bs.gap2, bs.pt5, bs.px4, bs.pb9)}>
				<ScheduleDateInput
					selectedDate={state.selectedDate}
					onDateChange={date => dispatch({ type: 'jumpToDateApplied', payload: date })}
					onValidityChange={setIsDateInputValid}
				/>
				<div className={css(bs.flex, bs.flex1)}>
					<div className={css(bs.flex, bs.itemsCenter, bs.gap2)}>
						{shouldShowScheduleTypeSelector ? (
							<Select
								value={state.sendNow ? 'now' : 'schedule'}
								onValueChange={value => dispatch({ type: 'setSendNow', payload: value === 'now' })}
							>
								<SelectTrigger>
									<span>
										{
											ScheduleSelectBoxOptions.find(x => x.value === (state.sendNow ? 'now' : 'schedule'))
												?.title as string
										}
									</span>
								</SelectTrigger>
								<SelectContent side='top'>
									{ScheduleSelectBoxOptions.map(option => (
										<SelectItem key={option.value} value={option.value}>
											{option.title as string}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						) : null}
						{!state.sendNow && timeOptions.length > 0 ? (
							<Combobox
								items={timeOptions}
								value={validSelectedHour?.value >= 0 ? validSelectedHour : null}
								onValueChange={item => {
									const option = item as ISelectBoxOption<number> | null;
									if (state.selectedDate && option?.value != null) {
										dispatch({
											type: 'scheduleHourChange',
											payload: option.value,
										});
									}
								}}
								itemToStringLabel={item => (item as ISelectBoxOption<number>).title as string}
								isItemEqualToValue={(a, b) =>
									(a as ISelectBoxOption<number>).value === (b as ISelectBoxOption<number>).value
								}
							>
								<ComboboxInput placeholder='Search time...' />
								<ComboboxContent side='top'>
									<ComboboxEmpty>No matching times</ComboboxEmpty>
									<ComboboxList>
										{(item: ISelectBoxOption<number>) => (
											<ComboboxItem key={item.value} value={item}>
												<ComboboxItemIndicator />
												{item.title as string}
											</ComboboxItem>
										)}
									</ComboboxList>
								</ComboboxContent>
							</Combobox>
						) : null}
						{!blogPost.status || blogPost.status === Api.BlogStatus.Draft || state.sendNow ? (
							<Button
								disabled={disableSendButton}
								onClick={() => {
									if (state.sendNow) {
										handleSendNowClick();
										return;
									}
									handleScheduleClick();
								}}
								isLoading={showIsLoading}
								label={<>{state.sendNow ? 'Publish Now' : 'Schedule'}</>}
							/>
						) : (
							<Button
								disabled={disableSendButton}
								onClick={() => hanldleChangeDateClick()}
								label='Change Date'
								isLoading={showIsLoading}
							/>
						)}
					</div>
				</div>
			</footer>
		</div>
	);
});
